import React, { useState } from 'react';
import { QrCode, LayoutDashboard, LogOut, User, CreditCard, Key, Shield, Menu, X, UserX, Image as ImageIcon, MessageSquarePlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { ROUTES } from '../../utils/constants';
import { adminService } from '../../api/services';
import { storage } from '../../utils/storage';
import { NotificationPane } from './NotificationPane';
import { FeedbackModal } from './FeedbackModal';
import { UnsavedChangesProvider, useUnsavedChangesGuard } from '../../contexts/UnsavedChangesContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

export function DashboardLayout(props: DashboardLayoutProps) {
  return (
    <UnsavedChangesProvider>
      <DashboardLayoutInner {...props} />
    </UnsavedChangesProvider>
  );
}

function DashboardLayoutInner({ children, currentPage = 'dashboard' }: DashboardLayoutProps) {
  const { user, logout, refreshUser } = useAuth();
  const { isAdmin, hasApiAccess, planDisplayName } = usePermissions();
  const navigate = useNavigate();
  const { guardNavigation } = useUnsavedChangesGuard();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Check if we're in impersonation mode
  const isImpersonating = !!localStorage.getItem('admin_original_token');

  const handleEndImpersonation = async () => {
    setEndingImpersonation(true);
    try {
      await adminService.endImpersonation();
      const adminToken = localStorage.getItem('admin_original_token');
      if (adminToken) {
        storage.setAccessToken(adminToken);
        localStorage.removeItem('admin_original_token');
        await refreshUser();
        toast.success('Ended impersonation session');
        navigate('/admin/users');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to end impersonation');
    } finally {
      setEndingImpersonation(false);
    }
  };

  // Build nav items dynamically based on permissions
  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.DASHBOARD },
    { id: 'qr-codes', icon: QrCode, label: 'QR Codes', path: ROUTES.QR_CODES },
    { id: 'images', icon: ImageIcon, label: 'My Images', path: ROUTES.IMAGES },
    { id: 'billing', icon: CreditCard, label: 'Billing', path: '/dashboard/billing' },
    // API Keys - only shown if user has API access (Business tier)
    ...(hasApiAccess ? [{ id: 'api-keys', icon: Key, label: 'API Keys', path: '/dashboard/api-keys' }] : []),
    { id: 'profile', icon: User, label: 'Profile', path: '/profile' },
    // Admin - only shown if user is admin
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin', path: '/admin/users' }] : []),
  ];

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.path && item.path !== '#') {
      guardNavigation(() => {
        navigate(item.path);
        setSidebarOpen(false); // Close sidebar on mobile after navigation
      });
    }
  };

  // position: fixed + inset-0 (not the in-flow h-screen this used to be): something
  // under this tree (a portaled Radix/tooltip element, react-hot-toast's Toaster, React
  // Query Devtools — never fully pinned down, and not worth more time chasing) was
  // making <body> itself real-scrollable by ~900px on long pages, so a scroll gesture
  // outside the Content Area div (hovering the subheader bar, an open modal) scrolled
  // <body> into its neon-lime background. A fixed, inset-0 element is structurally
  // incapable of contributing to an ancestor's scrollHeight, no matter what's inside
  // it or what else is happening elsewhere in <body> — a stronger guarantee than
  // fighting the actual cause via overflow/min-height, which didn't fully hold.
  return (
    <div className="fixed inset-0 flex overflow-hidden bg-white">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - High Contrast Navy */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-60 bg-[--clinqr-primary] text-[--clinqr-bg] flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-8 border-b border-[--clinqr-bg]/10">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => guardNavigation(() => navigate(ROUTES.HOME))}
            >
              <div className="w-10 h-10 bg-[--clinqr-bg]/10 rounded-lg flex items-center justify-center">
                <QrCode className="w-6 h-6 text-[--clinqr-bg]" strokeWidth={2} />
              </div>
              <span className="text-xl font-display text-[--clinqr-bg]">Clinqr</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[--clinqr-bg]" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === currentPage;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-[--clinqr-bg] text-[--clinqr-primary] shadow-lg'
                    : 'hover:bg-white/20 text-white/80 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={2} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="border-t border-[--clinqr-bg]/10">
          <div className="p-6 space-y-4">
            {/* Impersonation Indicator */}
            {isImpersonating && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-200 text-xs font-medium">
                  <UserX className="w-4 h-4" />
                  Impersonating User
                </div>
                <p className="text-red-300 text-xs mt-1">
                  You are viewing the app as {user?.email}
                </p>
              </div>
            )}

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[--clinqr-bg] flex items-center justify-center text-[--clinqr-primary] font-medium text-sm border-2 border-[--clinqr-bg]/20 flex-shrink-0">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[--clinqr-bg] truncate">{user?.email || 'User'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-[--clinqr-bg]/20 text-[--clinqr-bg] rounded-full">{planDisplayName}</span>
                  {isAdmin && (
                    <span className="text-xs px-2 py-0.5 bg-amber-500/30 text-amber-200 rounded-full">Admin</span>
                  )}
                </div>
              </div>
            </div>

            {/* End Impersonation Button */}
            {isImpersonating && (
              <button
                onClick={handleEndImpersonation}
                disabled={endingImpersonation}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {endingImpersonation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Ending...
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4" strokeWidth={2} />
                    End Impersonation
                  </>
                )}
              </button>
            )}

            {/* Feedback Button */}
            <button
              onClick={() => setFeedbackOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors border border-white/10 hover:border-white/40"
            >
              <MessageSquarePlus className="w-4 h-4" strokeWidth={2} />
              Send Feedback
            </button>

            {/* Logout Button */}
            <button
              onClick={() => logout()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors border border-white/10 hover:border-white/40"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
              {isImpersonating ? 'Logout & End Session' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Top Navigation Bar */}
        <div className="h-16 bg-white border-b border-[--clinqr-border]/10 flex items-center justify-between px-4 lg:px-8">
          {/* Left Side - Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-[--clinqr-bg-subtle] rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5 text-[--clinqr-text-secondary]" />
            </button>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Notifications */}
            <NotificationPane />
          </div>
        </div>

        {/* Subheader portal target: a non-scrolling row between the top bar and the
            padded Content Area, for pages that need a page-level toolbar (e.g. the QR
            editor's back/save/section-nav bar) to sit flush under the top bar with no
            padding to fight — see QRCodeEditForm's use of createPortal into this node. */}
        <div id="dashboard-subheader-root" />

        {/* Content Area */}
        {/* min-h-0: the actual root cause of the scroll-leaking-to-document bug. A flex
            item defaults to min-height:auto, which lets it grow to fit its content
            instead of shrinking to the space flex-1 allocates it — so on a long enough
            page (like the QR editor), this div was silently growing past the viewport
            and pushing real (not just elastic-bounce) overflow onto <body>, which any
            scroll gesture outside this div (hovering the subheader bar, an open modal)
            would then scroll into, revealing <body>'s neon-lime background.
            overscroll-contain stops rubber-band chaining, but min-h-0 stops the actual
            overflow from happening in the first place — both are needed. */}
        <div id="dashboard-content-area" className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[--clinqr-bg-subtle]/30 p-4 lg:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
